import React, { useContext, useEffect, useState } from 'react';
import { Card, Typography, Box, Divider } from '@mui/joy';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import TaskCard from './TaskCard';
import TaskDetailPanel from './TaskDetailPanel';

const MyTasksWidget = () => {
  const { user, profile } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Only show for staff on the status board
  const isStaff = profile && ['super-admin', 'admin', 'staff'].includes(profile.user_type) && profile.show_on_status_board;

  useEffect(() => {
    if (!isStaff || !user?.id) return;
    setLoading(true);
    supabase
      .from('tasks')
      .select('*')
      .eq('assigned_to', user.id)
      .eq('completed', false)
      .order('order_position', { ascending: true })
      .then(({ data, error }) => {
        if (!error) setTasks(data || []);
        setLoading(false);
      });
  }, [user, isStaff]);

  // Handle drag end for reordering
  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(tasks);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    // Update order_position locally
    const updatedTasks = reordered.map((task, idx) => ({ ...task, order_position: idx + 1 }));
    setTasks(updatedTasks);
    // Batch update order_position in DB
    const updates = updatedTasks.map(t => ({ id: t.id, order_position: t.order_position }));
    // Use upsert for batch update
    await supabase.from('tasks').upsert(updates, { onConflict: ['id'] });
  };
  if (!isStaff) {
    // Debug: show why the card is hidden
    return (
      <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden', p: 3, bgcolor: 'warning.50' }}>
        <Typography level="h5" sx={{ color: 'warning.700', mb: 1 }}>
          My Tasks (Debug Info)
        </Typography>
        <Typography level="body-sm" sx={{ color: 'warning.800' }}>
          This card is hidden because:
        </Typography>
        <ul style={{ color: '#b26a00', fontSize: '0.95em', marginTop: 8 }}>
          {!user && <li>User is not logged in.</li>}
          {!profile && <li>User profile is not loaded.</li>}
          {profile && !['super-admin', 'admin', 'staff'].includes(profile.user_type) && <li>User is not staff/admin/super-admin.</li>}
          {profile && !profile.show_on_status_board && <li>User is not set to show on status board.</li>}
        </ul>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 4, borderRadius: 'xl', overflow: 'hidden', p: 3 }}>
      <Typography level="h4" sx={{ color: 'success.700', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>task_alt</span>
        My Tasks
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {loading ? (
        <Typography level="body-sm">Loading...</Typography>
      ) : tasks.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>You have no active tasks assigned.</Typography>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="my-tasks-droppable">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {tasks.map((task, idx) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    index={idx}
                    onClick={() => { setSelectedTask(task); setDetailOpen(true); }}
                    canManageTasks={true}
                  />
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <TaskDetailPanel
        task={selectedTask}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onTaskUpdated={() => {
          setDetailOpen(false);
          // Refresh tasks after update
          supabase
            .from('tasks')
            .select('*')
            .eq('assigned_to', user.id)
            .eq('completed', false)
            .order('order_position', { ascending: true })
            .then(({ data, error }) => {
              if (!error) setTasks(data || []);
            });
        }}
        canManageTasks={true}
      />
    </Card>
  );
};

export default MyTasksWidget;
